import Chat from "@mui/icons-material/Chat";
import Dashboard from "@mui/icons-material/Dashboard";
import DomainAdd from "@mui/icons-material/DomainAdd";
import FindInPage from "@mui/icons-material/FindInPage";
import FolderOpen from "@mui/icons-material/FolderOpen";
import Help from "@mui/icons-material/Help";
import LibraryBooks from "@mui/icons-material/LibraryBooks";
import Notifications from "@mui/icons-material/Notifications";
import PlaylistAdd from "@mui/icons-material/PlaylistAdd";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import {
    Suspense,
    lazy,
    useEffect,
    useState,
    type PropsWithChildren,
} from "react";
import { useTranslation } from "react-i18next";

import { AppHeader } from "./AppHeader";
import type { NavItemType } from "./NavItem";
import { Sidebar } from "./Sidebar";

import { ArrowSplit, EcgHeart } from "@/components/icons";
import { NavDrawerProvider } from "@/contexts/NavDrawerProvider";
import { useAuthStore } from "@/stores/auth";
import { useEmailListStore } from "@/stores/emailList";
import { useSettingsStore } from "@/stores/settings";
import { usernameIsEmail } from "@/utils";

declare global {
    interface Window {
        Beamer?: {
            notificationNumber: number;
            toggle: () => void;
            update: (payload: {
                user_id?: string;
                user_firstname?: string;
                user_lastname?: string;
                filter: string;
            }) => void;
        };
    }
}

const FeedbackModal = lazy(() =>
    import("@/components/modals/FeedbackModal").then((module) => ({
        default: module.FeedbackModal,
    })),
);

const HelpModal = lazy(() =>
    import("@/components/modals/HelpModal").then((module) => ({
        default: module.HelpModal,
    })),
);

export function AppLayout({ children }: PropsWithChildren) {
    const { items } = useEmailListStore();
    const { user, logout } = useAuthStore();
    const { theme, setTheme } = useSettingsStore();
    const { t } = useTranslation();
    const [notificationCount, setNotificationCount] = useState(0);

    const firstName = usernameIsEmail(user) ? user?.first_name : user?.username;
    const lastName = usernameIsEmail(user)
        ? user?.last_name
        : `(${user?.organisation?.library})`;

    useEffect(() => {
        if (!window.Beamer) return;
        window.Beamer.update({
            user_id: user?.username,
            user_firstname: firstName,
            user_lastname: lastName,
            filter: `${user?.organisation?.id};${user?.organisation?.library}`,
        });
        // Mirror the Beamer notification count whenever the user context updates.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setNotificationCount(window.Beamer.notificationNumber || 0);
    }, [user, firstName, lastName]);

    const navItems: NavItemType[] = [
        { type: "link", to: "/", icon: <Dashboard />, label: t("nav.home") },
        {
            type: "link",
            to: "/browse",
            icon: <FindInPage />,
            label: t("nav.browse"),
        },
        {
            type: "link",
            to: "/resources",
            icon: <ArrowSplit />,
            label: t("nav.resources"),
        },
        { type: "divider" },
        items.length > 0
            ? {
                  type: "link",
                  to: "/email",
                  icon: (
                      <Badge color="primary" badgeContent={items.length}>
                          <PlaylistAdd />
                      </Badge>
                  ),
                  label: t("nav.email"),
              }
            : {
                  type: "link",
                  to: "/email",
                  icon: <PlaylistAdd />,
                  label: t("nav.email"),
                  visible: false,
              },
        ...(items.length > 0 ? [{ type: "divider" as const }] : []),
        {
            type: "function",
            label: t("nav.updates"),
            icon:
                notificationCount > 0 ? (
                    <Badge color="primary" badgeContent={notificationCount}>
                        <Notifications />
                    </Badge>
                ) : (
                    <Notifications />
                ),
            onClick: () => {
                if (window.Beamer) {
                    window.Beamer.toggle();
                }
            },
        },
        {
            type: "function",
            label: t("nav.procedure-list"),
            icon: <EcgHeart />,
            onClick: downloadProcedureList,
        },
        {
            type: "function",
            label: t("nav.feedback"),
            icon: <Chat />,
            onClick: () => setFeedbackOpen(true),
        },
        {
            type: "function",
            label: t("nav.help"),
            icon: <Help />,
            onClick: () => setHelpOpen(true),
        },
    ];

    if (["tm", "admin"].includes(user?.role as string)) {
        navItems.push(
            { type: "divider" },
            {
                type: "link",
                to: "/messages",
                icon: <QuestionAnswerIcon />,
                label: t("nav.messages"),
            },
            {
                type: "link",
                to: "/organisations",
                icon: <DomainAdd />,
                label: t("nav.organisations"),
            },
            {
                type: "link",
                to: "/collections",
                icon: <FolderOpen />,
                label: t("nav.collections"),
            },
            {
                type: "link",
                to: "/libraries",
                icon: <LibraryBooks />,
                label: t("nav.libraries"),
            },
        );
    }

    const [mobileOpen, setMobileOpen] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);

    function downloadProcedureList() {
        // Direct download of the canonical PDF
        const url =
            "https://inform-prod-assets.s3.eu-west-1.amazonaws.com/ProcedureLists/EIDO%20Healthcare%20Master%20Procedure%20List.pdf";
        window.open(url, "_blank");
    }

    return (
        <NavDrawerProvider
            open={() => setMobileOpen(true)}
            close={() => setMobileOpen(false)}
        >
            <Box sx={{ display: "flex", height: "100vh" }}>
                <Sidebar
                    user={user}
                    theme={theme}
                    setTheme={setTheme}
                    logout={logout}
                    items={navItems}
                    mobileOpen={mobileOpen}
                    onCloseMobile={() => setMobileOpen(false)}
                />

                <Suspense fallback={null}>
                    {feedbackOpen ? (
                        <FeedbackModal
                            open={feedbackOpen}
                            onClose={() => setFeedbackOpen(false)}
                        />
                    ) : null}
                </Suspense>
                <Suspense fallback={null}>
                    {helpOpen ? (
                        <HelpModal
                            open={helpOpen}
                            onClose={() => setHelpOpen(false)}
                        />
                    ) : null}
                </Suspense>

                <Box
                    sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                        minHeight: 0,
                    }}
                >
                    <AppHeader onMenuClick={() => setMobileOpen(true)} />
                    <Box
                        component="main"
                        sx={{ p: 2, flex: 1, minWidth: 0, minHeight: 0 }}
                    >
                        {children}
                    </Box>
                </Box>
            </Box>
        </NavDrawerProvider>
    );
}
